import { useChatApi as useAgentChatApi } from "@ui/agent/components/agent/api/chat-api";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useChatsContext } from "@/lib/context/ChatsProvider/hooks/useChatsContext";
import { useAxios, useToast } from "@/lib/hooks";
import { ThreadEntity } from "@/lib/types/Thread";

export const useChatsListItem = (chat: ThreadEntity) => {
  const location = useLocation();
  const pathname = location.pathname?.split("/").at(-1);
  const selected = chat.chat_id === pathname;
  const [chatName, setChatName] = useState(chat.chat_name);
  const [editingName, setEditingName] = useState(false);

  useEffect(() => {
    if (!editingName) {
      setChatName(chat.chat_name);
    }
  }, [chat.chat_name, editingName]);
  const { publish } = useToast();
  const { setAllChats } = useChatsContext();
  const navigate = useNavigate();
  const { t } = useTranslation(["chat"]);
  const { axiosInstance } = useAxios();
  const { deleteThread, updateThread } = useAgentChatApi(axiosInstance);

  const deleteChatHandler = async () => {
    const chatId = chat.chat_id;
    try {
      await deleteThread(chatId);
      setAllChats((chats) =>
        chats.filter((currentChat) => currentChat.chat_id !== chatId),
      );

      const isDeletedChatRoute = window.location.pathname.includes(
        `/chat/${chatId}`,
      );

      if (selected || isDeletedChatRoute) {
        void navigate("/", { replace: true });
      }
      publish({
        text: t("chatDeleted", { id: chatId, ns: "chat" }),
        variant: "success",
      });
    } catch (error) {
      console.error(t("errorDeleting", { error: error, ns: "chat" }));
      publish({
        text: t("errorDeleting", { error: error, ns: "chat" }),
        variant: "danger",
      });
    }
  };

  const updateChatName = async () => {
    if (chatName !== chat.chat_name) {
      await updateThread(chat.chat_id, { name: chatName });
      setAllChats((chats) =>
        chats.map((c) =>
          c.chat_id === chat.chat_id ? { ...c, chat_name: chatName } : c,
        ),
      );
    }
  };

  const toggleFavorite = async () => {
    const newValue = !chat.is_favorite;
    setAllChats((chats) =>
      chats.map((c) =>
        c.chat_id === chat.chat_id ? { ...c, is_favorite: newValue } : c,
      ),
    );
    try {
      await updateThread(chat.chat_id, { is_favorite: newValue });
    } catch (error) {
      setAllChats((chats) =>
        chats.map((c) =>
          c.chat_id === chat.chat_id ? { ...c, is_favorite: !newValue } : c,
        ),
      );
      publish({
        text: t("errorUpdating", { error, ns: "chat" }),
        variant: "danger",
      });
    }
  };

  const startEditing = () => {
    if (!editingName) {
      setEditingName(true);
    }
  };

  const finishEditing = () => {
    if (editingName) {
      setEditingName(false);
      void updateChatName();
    }
  };

  const cancelEditing = () => {
    setChatName(chat.chat_name);
    setEditingName(false);
  };

  return {
    setChatName,
    editingName,
    chatName,
    selected,
    isFavorite: chat.is_favorite,
    startEditing,
    finishEditing,
    cancelEditing,
    deleteChat: deleteChatHandler,
    toggleFavorite,
  };
};
